"use client";

import React, { useEffect, useState, useCallback, ReactNode } from "react";
import { Link } from "@/i18n/routing";
import { Moon, Sun, Mail, Phone, Grid, BookOpen } from "lucide-react";
import { useThemeToggle } from "@/components/theme/useThemeToggle";

interface LandingPageClientProps {
  children: ReactNode;
}

export default function LandingPageClient({ children }: LandingPageClientProps) {
  const { isDark, toggle: toggleTheme } = useThemeToggle();
  const [cookieAccepted, setCookieAccepted] = useState(true);

  useEffect(() => {
    // Cookie consent check
    const consent = localStorage.getItem("optistack_cookie_consent");
    setCookieAccepted(consent === "accepted");
  }, []);

  const acceptCookies = useCallback(() => {
    localStorage.setItem("optistack_cookie_consent", "accepted");
    setCookieAccepted(true);
  }, []);

  return (
    // No colour transition on this scroller or the nav: Chrome ran the page
    // background's transition on its own clock and could leave it in the old
    // theme under everything else (see src/lib/theme.ts).
    <div className="scroll-smooth bg-[#F7F8FA] dark:bg-[#0f172a] text-slate-900 dark:text-slate-100 antialiased min-h-screen overflow-auto">
      {/* Navbar - client-side for theme toggle */}
      <nav className="sticky top-0 z-50 bg-[#F7F8FA]/70 dark:bg-[#0f172a]/60 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#d47723] rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-md shadow-orange-500/15">
              O
            </div>
            <span className="text-lg sm:text-xl font-extrabold tracking-[-0.02em] leading-none text-slate-900 dark:text-white">
              OptiStack
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-4">
            <a
              href="/downloads/optistack-operator-guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-[#d47723] dark:hover:text-[#d47723] transition-colors text-sm font-medium p-2 sm:p-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 sm:hover:bg-transparent"
              title="Operator User Guide (PDF)"
            >
              <BookOpen className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">User Guide</span>
            </a>
            <Link
              href="/features"
              className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-[#d47723] dark:hover:text-[#d47723] transition-colors text-sm font-medium p-2 sm:p-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 sm:hover:bg-transparent"
              title="Features"
            >
              <Grid className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">Features</span>
            </Link>
            <a
              href="mailto:info@maxwellslitters.com"
              className="flex items-center gap-1.5 text-slate-500/80 dark:text-slate-400/80 hover:text-[#d47723] dark:hover:text-[#d47723] transition-colors p-2 sm:p-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 sm:hover:bg-transparent"
              title="Email us"
            >
              <Mail className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              <span className="hidden md:inline font-mono text-[11px]">info@maxwellslitters.com</span>
            </a>
            <a
              href="tel:+918264621011"
              className="flex items-center gap-1.5 text-slate-500/80 dark:text-slate-400/80 hover:text-[#d47723] dark:hover:text-[#d47723] transition-colors p-2 sm:p-0 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 sm:hover:bg-transparent"
              title="Call us"
            >
              <Phone className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              <span className="hidden lg:inline font-mono text-[11px]">+91 82646-21011</span>
            </a>
            <span className="hidden md:inline h-4 w-px bg-slate-200/70 dark:bg-slate-700/70"></span>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link
              className="bg-[#d47723] hover:bg-[#c06a1f] text-white px-3 sm:px-4 py-2 rounded-full transition-all shadow-md shadow-orange-500/15 border border-orange-500/20 hover:border-orange-500/30 text-sm font-medium"
              href="/login"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Server-rendered content passed as children */}
      {children}

      {/* Cookie Consent Banner */}
      {!cookieAccepted && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-xl mx-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg shadow-slate-900/10 dark:shadow-slate-900/50 px-5 py-4 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left leading-relaxed flex-1">
              Our website uses cookies, which helps us to deliver the best customer
              experience.
              <Link
                href="/privacy"
                className="text-slate-700 dark:text-slate-300 underline underline-offset-2 hover:text-[#d47723] dark:hover:text-[#d47723] transition-colors ml-1"
              >
                Cookie policy
              </Link>
              .
            </p>
            <button
              onClick={acceptCookies}
              className="bg-[#d47723] hover:bg-[#c06a1f] text-white text-xs sm:text-sm font-semibold px-5 py-2 rounded-full transition-all shadow-sm shadow-orange-500/20 whitespace-nowrap flex-shrink-0"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      <a
        href="https://api.whatsapp.com/send?phone=918264621011"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-[99] w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all hover:scale-110"
      >
        <svg
          viewBox="0 0 32 32"
          fill="white"
          className="w-7 h-7"
          aria-hidden="true"
        >
          <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.962A15.923 15.923 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.342 22.616c-.39 1.1-1.932 2.012-3.178 2.278-.852.18-1.964.324-5.71-1.226-4.796-1.984-7.882-6.852-8.122-7.168-.23-.316-1.932-2.574-1.932-4.908 0-2.334 1.222-3.48 1.656-3.958.39-.432 1.012-.632 1.608-.632.194 0 .368.01.524.018.434.02.652.046.938.726.358.852 1.226 2.99 1.332 3.21.108.22.216.518.068.808-.138.3-.258.434-.478.688-.22.254-.428.448-.648.722-.2.244-.424.506-.18.98.244.468 1.086 1.79 2.332 2.902 1.604 1.432 2.916 1.89 3.384 2.094.358.156.784.118 1.038-.156.322-.346.72-.918 1.124-1.482.288-.402.65-.452 1.046-.302.4.142 2.53 1.194 2.964 1.412.434.218.724.324.83.508.108.182.108 1.064-.282 2.164z"/>
        </svg>
      </a>
    </div>
  );
}
